import { useCallback } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { LocationGroupWithAllocation } from '~/ui/pages/ActTeam/reusable/ClientAllocationForm/shared/model/types';
import { IAddAllocationFormValues } from '~/ui/pages/ActTeam/reusable/ClientAllocationForm/AddClientAllocation/types';

const getFieldPaths = (teamMemberIdx: number) => ({
  allocations: `teamMembersAllocations.${teamMemberIdx}.locationGroupAllocations` as const,
  selectedIds: `teamMembersAllocations.${teamMemberIdx}.selectedLocationGroupIds` as const,
});

const useLocationGroupAllocationsUpdate = (teamMemberIdx: number) => {
  const { control, getValues, setValue } = useFormContext<IAddAllocationFormValues>();
  const { allocations: allocationsPath, selectedIds: selectedIdsPath } =
    getFieldPaths(teamMemberIdx);

  const { fields, replace } = useFieldArray({
    control,
    name: allocationsPath,
  });

  const updateLocationGroupAllocations = useCallback(
    (selectedGroups: LocationGroupWithAllocation[]) => {
      const currentAllocations = getValues(allocationsPath) ?? [];
      const currentById = new Map(currentAllocations.map(lg => [lg.locationGroupId, lg]));

      const next = selectedGroups.map(
        selected => currentById.get(selected.locationGroupId) ?? selected,
      );

      replace(next);
      setValue(
        selectedIdsPath,
        next.map(lg => lg.locationGroupId),
        { shouldDirty: true },
      );
    },
    [allocationsPath, getValues, replace, selectedIdsPath, setValue],
  );

  const removeLocationGroupAt = useCallback(
    (idx: number) => {
      const currentAllocations = getValues(allocationsPath) ?? [];
      const next = currentAllocations.filter((_, i) => i !== idx);

      replace(next);
      setValue(
        selectedIdsPath,
        next.map(lg => lg.locationGroupId),
        { shouldDirty: true },
      );
    },
    [allocationsPath, getValues, replace, selectedIdsPath, setValue],
  );

  const removeClientFromGroup = useCallback(
    (fieldIdx: number, clientId: number) => {
      const currentAllocations = getValues(allocationsPath) ?? [];
      const next = currentAllocations.map((lg, i) => {
        if (i !== fieldIdx) return lg;
        return { ...lg, removedClientIds: [...(lg.removedClientIds ?? []), clientId] };
      });
      replace(next);
    },
    [allocationsPath, getValues, replace],
  );

  return { fields, updateLocationGroupAllocations, removeLocationGroupAt, removeClientFromGroup };
};

export default useLocationGroupAllocationsUpdate;
